import { DateTime, ExprForm } from '@mode-switch/express';
import { Attr } from '@mode-switch/model';
import { EncFilterField, FilterType, SelectionMode, VariableType } from '@mode-vegas/vegas';
import { FeatureFlag, FeatureFlagsFacade } from '@mode/shared/contract-common';
import { formatInUTC } from '@mode/shared/util-js';
import { FormulaOperations } from '@mode/shared/util-switch';
import * as angular from 'angular';
import { startOfWeek } from 'date-fns';
import { first } from 'rxjs/operators';
import { FieldComponent, FieldController } from './field.component';
import { FieldIcons } from './viz-designer-icons';

class FilterFieldController extends FieldController {
  static $inject = ['$window', '$scope', '$element', 'RxStoreService', 'FeatureFlagsFacade'];
  disableAggregate: boolean;
  private customCalendarSettingsEnabled = false;
  constructor(
    $window: angular.IWindowService,
    readonly $scope: angular.IScope,
    private readonly $element: angular.IAugmentedJQuery,
    private readonly store,
    private readonly featureFlagsFacade: FeatureFlagsFacade
  ) {
    super();
    this.isSortAllowed = false;
    this.handleContextSubMenuPositioning(this.$scope);
    this.featureFlagsFacade
      .asObservable(FeatureFlag.CustomCalendar)
      .pipe(first())
      .subscribe((flag) => (this.customCalendarSettingsEnabled = flag));
  }

  /**
   * Better positioning for quick chart context menu submenus.
   * If it doesn't fit on the right, position it to the left
   */
  handleContextSubMenuPositioning($scope: angular.IScope) {
    type EventArg = {
      contextMenu: JQuery;
      params: {
        view: Window;
        level: number;
        event: {
          event: MouseEvent;
          pageX: number;
          pageY: number;
        };
      };
    };

    $scope.$on('context-menu-opened', onContextMenuOpened);
    function onContextMenuOpened(e: angular.IAngularEvent, { contextMenu, params }: EventArg) {
      // Bail if this isn't at our submenu depth
      if (params.level !== 1) return;

      // Bail if this isn't our menu
      if (!contextMenu.hasClass('context-menu-quick-charts')) return;
      contextMenu.addClass('qc-submenu');

      // Original mouse event
      const event = params.event.event;
      // Context menu item that triggered the submenu
      const target = angular.element(event.target).parents('li');
      // Bounding box of said context menu item, if we can get it
      const clientRect = target.get(0)?.getBoundingClientRect();

      // If we have a bounding box for the context menu item that triggered the submenu then we'll be able
      // to figure out how to position said submenu. If there isn't enough room to the right of the context
      // menu to position the submenu, ui.bootstrap.contextMenu will just overlay the context menu and it's
      // janky looking.. if there isn't enough room to the right, reposition it on the left (like @perfectmemory/ngx-contextmenu)
      if (clientRect) {
        const viewWidth = event.view.innerWidth + window.pageXOffset;
        const menuWidth = contextMenu.outerWidth();

        // Not enough room on the right, position to the left
        if (viewWidth - clientRect.right < menuWidth + 2) {
          params.event.pageX = clientRect.x - menuWidth - 1;
          params.event.pageY = clientRect.y;
        }
      }
    }
  }

  // bindings
  readonly field: EncFilterField;

  readonly onTriggerEdit: (args: { $encFilterField: EncFilterField; $oldFilter: EncFilterField }) => void;
  readonly onVarChange: (args: { $field: EncFilterField; $varType: VariableType }) => void;
  readonly onRemove: (args: { $field: EncFilterField }) => void;

  triggerEdit($encFilterField = this.field, $oldFilter = this.field) {
    this.onTriggerEdit({
      $encFilterField,
      $oldFilter,
    });
  }

  handleDoubleClick() {
    this.triggerEdit();
  }

  $onChanges(changes: angular.IOnChangesObject) {
    super.$onChanges(changes);

    if (this.disableAggregate && this.formula.isNonScalarCalc) {
      this.isValid = false;
      this.error = 'Aggregate/Analytic filters are not supported on this visualization type';
      this.contextMenuOptions = this.buildContextMenuOptions();
    } else if (!this.disableAggregate && this.formula.isAnalyticCalc) {
      this.isValid = false;
      this.error = 'Analytic filters are not yet supported';
      this.contextMenuOptions = this.buildContextMenuOptions();
    }
  }

  $postLink() {
    this.$element[0].addEventListener('dragstart', (evt: any) => {
      if (evt.dataTransfer) {
        evt.dataTransfer.setData('text/plain', evt.target.id);
      }
    });
  }

  buildContextMenuOptions() {
    if (!this.isValid) {
      return [
        {
          html: this.getMenuItemHtml(false, 'Remove'),
          click: (e) => this.onRemove({ $field: this.field }),
        },
      ];
    }

    const opts: {
      html: string;
      click?: () => void;
      enabled?: () => boolean;
      children?: { html: string; click: () => void }[];
    }[] = [
      {
        html: this.getMenuItemHtml(false, 'Edit Filter...'),
        click: () => this.triggerEdit(),
      },
      null,
    ];

    const isDateType = this.formula.isAttrDateType;
    if (isDateType) {
      const now = new Date();
      const dateTimeUnit = this.formula.dateFuncUnit;
      const isDateTrunc = this.formula.hasDateTrunc;
      const isDatePart = this.formula.hasDatePart;

      const setDateTrunc = (unit: DateTime.Unit) => {
        if (dateTimeUnit === unit && isDateTrunc) {
          return;
        }

        const source = FormulaOperations.dateTrunc(this.formula, unit);
        const varType = isDateTrunc ? this.formula.varType : Attr.Type.Continuous;
        this.setFormula(source, varType);
      };

      opts.push(
        {
          html: this.getMenuHeadingHtml('Dates and Times', FieldIcons.continuousDate),
          click: () => false,
          enabled: () => false,
        },
        {
          html: this.getMenuItemHtml(
            isDateTrunc && dateTimeUnit === DateTime.Unit.Year,
            'Year',
            formatInUTC(now, 'yyyy')
          ),
          click: () => setDateTrunc(DateTime.Unit.Year),
        },
        {
          html: this.getMenuItemHtml(
            isDateTrunc && dateTimeUnit === DateTime.Unit.Quarter,
            'Quarter',
            formatInUTC(now, 'QQQ yyyy')
          ),
          click: () => setDateTrunc(DateTime.Unit.Quarter),
        },
        {
          html: this.getMenuItemHtml(
            isDateTrunc && dateTimeUnit === DateTime.Unit.Month,
            'Month',
            formatInUTC(now, 'MMM yyyy')
          ),
          click: () => setDateTrunc(DateTime.Unit.Month),
        },
        {
          html: this.getMenuItemHtml(
            isDateTrunc && dateTimeUnit === DateTime.Unit.Week,
            'Week of',
            formatInUTC(startOfWeek(now), 'MMM d yyyy')
          ),
          click: () => setDateTrunc(DateTime.Unit.Week),
        },
        {
          html: this.getMenuItemHtml(
            isDateTrunc && dateTimeUnit === DateTime.Unit.Day,
            'Day',
            formatInUTC(now, 'MMM d yyyy')
          ),
          click: () => setDateTrunc(DateTime.Unit.Day),
        },
        null
      );

      const setDatePart = (unit: DateTime.Unit) => {
        if (dateTimeUnit === unit && isDatePart) {
          return;
        }

        const source = FormulaOperations.datePart(this.formula, unit);
        const varType = isDatePart ? this.formula.varType : Attr.Type.Discrete;
        this.setFormula(source, varType);
      };

      opts.push(
        {
          html: this.getMenuHeadingHtml('Date Parts', FieldIcons.discreteDate),
          click: () => false,
          enabled: () => false,
        },
        {
          html: this.getMenuItemHtml(
            isDatePart && dateTimeUnit === DateTime.Unit.Year,
            'Year',
            formatInUTC(now, 'yyyy')
          ),
          click: () => setDatePart(DateTime.Unit.Year),
        },
        {
          html: this.getMenuItemHtml(
            isDatePart && dateTimeUnit === DateTime.Unit.Quarter,
            'Quarter',
            formatInUTC(now, 'QQQ')
          ),
          click: () => setDatePart(DateTime.Unit.Quarter),
        },
        {
          html: this.getMenuItemHtml(
            isDatePart && dateTimeUnit === DateTime.Unit.Month,
            'Month',
            formatInUTC(now, 'MMM')
          ),
          click: () => setDatePart(DateTime.Unit.Month),
        },
        {
          html: this.getMenuItemHtml(
            isDatePart && dateTimeUnit === DateTime.Unit.Weekday,
            'Weekday',
            formatInUTC(now, 'ccc')
          ),
          click: () => setDatePart(DateTime.Unit.Weekday),
        },
        {
          html: this.getMenuItemHtml(isDatePart && dateTimeUnit === DateTime.Unit.Day, 'Day', formatInUTC(now, 'd')),
          click: () => setDatePart(DateTime.Unit.Day),
        }
      );

      opts.push(
        {
          html: this.getMenuItemHtml(isDatePart && dateTimeUnit === DateTime.Unit.Hour, 'Hour', formatInUTC(now, 'h')),
          click: () => setDatePart(DateTime.Unit.Hour),
        },
        {
          html: this.getMenuItemHtml(
            isDatePart && dateTimeUnit === DateTime.Unit.Minute,
            'Minute',
            formatInUTC(now, 'm')
          ),
          click: () => setDatePart(DateTime.Unit.Minute),
        },
        {
          html: this.getMenuItemHtml(
            isDatePart && dateTimeUnit === DateTime.Unit.Second,
            'Second',
            formatInUTC(now, 's')
          ),
          click: () => setDatePart(DateTime.Unit.Second),
        }
      );

      // Custom start of year options
      opts.push(null, ...this.buildYearStartMenuOpts(), ...this.buildWeekStartMenuOpts());

      opts.push(
        null,
        {
          html: this.getMenuItemHtml(this.formula.form === ExprForm.Access, 'Exact Date and Time'),
          click: () => {
            if (this.formula.form === ExprForm.Access) {
              return;
            }

            const source = FormulaOperations.access(this.formula);
            this.setFormula(source);
          },
        },
        null
      );
    }

    /// Integers, Numbers, Timestamp

    const isNumberOrDateType = this.formula.isAttrDateType || this.formula.isAttrNumberType;
    if (isNumberOrDateType === true) {
      opts.push(
        {
          html: this.getMenuItemHtml(this.formula.varType === Attr.Type.Discrete, 'Discrete'),
          click: () => {
            if (
              this.field.filterType === FilterType.Continuous ||
              this.field.filterType === FilterType.ContinuousDate ||
              this.field.filterType === FilterType.RelativeDate
            ) {
              this.onVarChange({ $field: this.field, $varType: VariableType.DISCRETE });
            }
          },
        },
        {
          html: this.getMenuItemHtml(this.formula.varType === Attr.Type.Continuous, 'Continuous'),
          click: () => {
            if (this.field.filterType === FilterType.Discrete) {
              this.onVarChange({ $field: this.field, $varType: VariableType.CONTINUOUS });
            }
          },
          enabled: () => {
            if (!this.formula.isValid) {
              return false;
            }

            return true;
          },
        },
        null
      );
    }

    opts.push({
      html: this.getMenuItemHtml(false, 'Remove'),
      click: () => this.onRemove({ $field: this.field }),
    });

    return opts;
  }

  buildWeekStartMenuOpts() {
    const weekStarts = [
      { value: DateTime.WeekStart.Sunday, name: 'Sunday' },
      { value: DateTime.WeekStart.Monday, name: 'Monday' },
      { value: DateTime.WeekStart.Tuesday, name: 'Tuesday' },
      { value: DateTime.WeekStart.Wednesday, name: 'Wednesday' },
      { value: DateTime.WeekStart.Thursday, name: 'Thursday' },
      { value: DateTime.WeekStart.Friday, name: 'Friday' },
      { value: DateTime.WeekStart.Saturday, name: 'Saturday' },
    ];

    return [
      {
        html: this.getMenuItemHtml(false, 'Week start', '<span class="context-item-submenu-arrow">▶</span>'),
        children: weekStarts.map(({ value, name }) => {
          const asideText =
            this.customCalendarSettingsEnabled && this.defaultWeekStart === value ? 'Org Default' : undefined;
          return {
            html: this.getMenuItemHtml(this.weekStart === value, name, asideText),
            click: () => this.setWeekStart(value),
          };
        }),
      },
    ];
  }

  buildYearStartMenuOpts() {
    const yearStarts = [
      { value: DateTime.YearStart.January, name: 'January' },
      { value: DateTime.YearStart.February, name: 'February' },
      { value: DateTime.YearStart.March, name: 'March' },
      { value: DateTime.YearStart.April, name: 'April' },
      { value: DateTime.YearStart.May, name: 'May' },
      { value: DateTime.YearStart.June, name: 'June' },
      { value: DateTime.YearStart.July, name: 'July' },
      { value: DateTime.YearStart.August, name: 'August' },
      { value: DateTime.YearStart.September, name: 'September' },
      { value: DateTime.YearStart.October, name: 'October' },
      { value: DateTime.YearStart.November, name: 'November' },
      { value: DateTime.YearStart.December, name: 'December' },
    ];

    return [
      {
        html: this.getMenuItemHtml(false, 'Year start', '<span class="context-item-submenu-arrow">▶</span>'),
        children: yearStarts.map(({ value, name }) => {
          const asideText =
            this.customCalendarSettingsEnabled && this.defaultYearStart === value ? 'Org Default' : undefined;
          return {
            html: this.getMenuItemHtml(this.yearStart === value, name, asideText),
            click: () => this.setYearStart(value),
          };
        }),
      },
    ];
  }

  setWeekStart(weekStart: DateTime.WeekStart): void {
    this.setWeekOrYearStart(weekStart, undefined);
  }

  setYearStart(yearStart: DateTime.YearStart): void {
    this.setWeekOrYearStart(this.weekStart, yearStart);
  }

  setWeekOrYearStart(weekStart?: DateTime.WeekStart, yearStart?: DateTime.YearStart): void {
    let source = '';
    let varType = this.formula.varType;

    if (this.formula?.dateFuncUnit) {
      const dateFunc = this.isDateTrunc ? FormulaOperations.dateTrunc : FormulaOperations.datePart;
      source = dateFunc(this.formula, this.formula.dateFuncUnit, weekStart, yearStart);
    } else if (this.formula?.isAttrDateType) {
      const dateFuncUnit = weekStart ? DateTime.Unit.Week : DateTime.Unit.Year;
      source = FormulaOperations.dateTrunc(this.formula, dateFuncUnit, weekStart, yearStart);
    }

    this.setFormula(source, varType);
  }

  setFormula = (formula: string, varType = this.formula.varType) => {
    const newFormula = this.formula.withNewFormula(formula, varType);
    if (newFormula.varType === Attr.Type.Continuous) {
      let filterType = FilterType.Continuous;
      if (newFormula.isFormulaDate) {
        filterType = FilterType.ContinuousDate;
      }

      this.triggerEdit(
        {
          filterType,
          variable: { type: VariableType.CONTINUOUS },
          formula,
          range: { min: null, max: null },
          includeMin: true,
          includeMax: true,
          includeNulls: false,
        },
        this.field
      );
    } else {
      this.triggerEdit(
        {
          filterType: FilterType.Discrete,
          variable: { type: VariableType.DISCRETE },
          formula,
          selection: {
            selected: [],
            mode: SelectionMode.ALL,
          },
        },
        this.field
      );
    }
  };

  get isDatePart(): boolean {
    return this.formula.form === ExprForm.DatePart;
  }

  get hasDateFunc(): boolean {
    return this.isDatePart || this.isDateTrunc;
  }

  get isDateTrunc(): boolean {
    return this.formula?.form === ExprForm.DateTrunc;
  }

  get weekStart(): DateTime.WeekStart | undefined {
    if (this.hasDateFunc) {
      return this.formula.weekStart ?? this.defaultWeekStart;
    }

    return;
  }

  get yearStart(): DateTime.YearStart | undefined {
    if (this.hasDateFunc) {
      return this.formula.yearStart ?? this.defaultYearStart;
    }

    return;
  }

  get defaultWeekStart(): DateTime.WeekStart | undefined {
    return this.formula.defaultWeekStart;
  }

  get defaultYearStart(): DateTime.YearStart | undefined {
    return this.formula.defaultYearStart;
  }
}

export const FilterFieldComponent: angular.IComponentOptions = {
  ...FieldComponent,
  bindings: {
    ...FieldComponent.bindings,
    disableAggregate: '<',
    onTriggerEdit: '&',
    onRemove: '&',
    onVarChange: '&',
  },
  controller: FilterFieldController,
};
